import {
  Button,
  ChevronRightIcon,
  DismissIcon,
  Flex,
  Typography,
  useDrawerContext,
} from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';

import { BottomSheet } from '../../../bottomSheet/bottomSheet';
import type { ReviewableProduct } from '../../reviewableProduct';
import { ReviewProductHeader } from '../reviewProductHeader/reviewProductHeader';

import styles from './reviewPrompt.module.css';

/** Most products the prompt lists at once. */
export const REVIEW_PROMPT_MAX_PRODUCTS = 3;

export interface ReviewPromptProps {
  /** Bottom sheet on mobile, floating card otherwise. */
  isMobile: boolean;
  /** Caps how many products are listed. Defaults to `REVIEW_PROMPT_MAX_PRODUCTS`. */
  maxProducts?: number;
  /** X, Esc, and (on mobile) tapping the overlay. */
  onDismiss: () => void;
  onReviewProduct: (product: ReviewableProduct) => void;
  open: boolean;
  /** Listed in the order given, most recent purchase first. */
  products: ReviewableProduct[];
  testId?: string;
}

interface ReviewPromptContentProps {
  onDismiss: () => void;
  onReviewProduct: (product: ReviewableProduct) => void;
  products: ReviewableProduct[];
  testId: string;
  titleId: string;
}

function ReviewPromptContent({
  onDismiss,
  onReviewProduct,
  products,
  testId,
  titleId,
}: ReviewPromptContentProps) {
  return (
    <Flex flexDirection="column" gap={24}>
      <Flex alignItems="start" gap={16} justifyContent="space-between">
        <Flex flexDirection="column">
          <Typography id={titleId} variant="h3-semibold">
            {products.length === 1
              ? 'You have a product to review!'
              : 'You have products to review!'}
          </Typography>
          <Typography>Help your community by reviewing products.</Typography>
        </Flex>
        <Button.Icon
          className={styles['close']}
          icon={<DismissIcon />}
          label="Close"
          onClick={onDismiss}
        />
      </Flex>
      <ul className={styles['list']}>
        {products.map((product) => (
          <li
            className={styles['product']}
            data-testid={`${testId}-product-${product.id}`}
            key={product.id}
          >
            <ReviewProductHeader
              brand={product.brand}
              category={product.category}
              imageUrl={product.imageUrl}
              name={product.name}
              nameMaxLines={2}
              size="compact"
              testId={`${testId}-product-${product.id}-header`}
            />
            {/* Stretched over the whole card, so the card is one click target. */}
            <button
              aria-label={`Review ${product.name}`}
              className={styles['reviewButton']}
              onClick={() => onReviewProduct(product)}
              type="button"
            >
              <span className={styles['chevron']}>
                <ChevronRightIcon size="sm" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Flex>
  );
}

interface CardProps {
  children: ReactNode;
  onDismiss: () => void;
  testId: string;
  titleId: string;
}

/** Non-modal desktop card. Esc dismisses only while focus is inside it. */
function ReviewPromptCard({ children, onDismiss, testId, titleId }: CardProps) {
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };

    card.addEventListener('keydown', handleKeyDown);
    return () => card.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  return (
    <aside
      aria-labelledby={titleId}
      className={styles['card']}
      data-testid={testId}
      ref={cardRef}
    >
      {children}
    </aside>
  );
}

/** Routes the X through the sheet so it slides out like an overlay tap. */
function SheetContent(props: Omit<ReviewPromptContentProps, 'onDismiss'>) {
  const { onRequestClose } = useDrawerContext();

  return <ReviewPromptContent {...props} onDismiss={onRequestClose} />;
}

/**
 * "You have products to review" prompt. Non-modal floating card on desktop,
 * which the caller positions; modal bottom sheet on mobile. Never closes
 * itself: the caller owns `open` and decides what dismissing or picking a
 * product does.
 */
export function ReviewPrompt({
  isMobile,
  maxProducts = REVIEW_PROMPT_MAX_PRODUCTS,
  onDismiss,
  onReviewProduct,
  open,
  products,
  testId = 'review-prompt',
}: ReviewPromptProps) {
  const titleId = useId();
  const visibleProducts = products.slice(0, maxProducts);

  if (visibleProducts.length === 0) return null;

  const contentProps = {
    onReviewProduct,
    products: visibleProducts,
    testId,
    titleId,
  };

  if (isMobile) {
    return (
      <BottomSheet
        aria-labelledby={titleId}
        data-testid={testId}
        dimOverlay={false}
        onRequestClose={onDismiss}
        open={open}
      >
        <div className={styles['sheetBody']}>
          <SheetContent {...contentProps} />
        </div>
      </BottomSheet>
    );
  }

  if (!open) return null;

  return (
    <ReviewPromptCard onDismiss={onDismiss} testId={testId} titleId={titleId}>
      <ReviewPromptContent {...contentProps} onDismiss={onDismiss} />
    </ReviewPromptCard>
  );
}
